import React, { useState, useEffect } from "react";
import axiosInstance from "../axiosConfig";
import DataTable from "react-data-table-component";
import { Plus, Edit, Trash2, Save, X, Search } from "lucide-react";
import toast from "react-hot-toast";

const OCRQtyMasterModal = ({ show, onClose }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [showAddModal, setShowAddModal] = useState(false);
  const [newGroup, setNewGroup] = useState({ groupName: "", qtyPerRow: "" });

  const [editRow, setEditRow] = useState(null);
  const [editQty, setEditQty] = useState("");

  useEffect(() => {
    if (show) {
      fetchData();
    }
  }, [show]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/api/purchase/ocr-qty-master");
      if (response.data.success) {
        setData(response.data.data);
      }
    } catch (error) {
      toast.error("Failed to load OCR Qty Master data.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!newGroup.groupName || !newGroup.qtyPerRow) return;
    try {
      const response = await axiosInstance.post("/api/purchase/ocr-qty-master", newGroup);
      if (response.data.success) {
        toast.success(response.data.message);
        setShowAddModal(false);
        setNewGroup({ groupName: "", qtyPerRow: "" });
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error adding record");
    }
  };

  const handleEditClick = (row) => {
    setEditRow(row.GROUPNAME);
    setEditQty(row.QTYPERROW);
  };

  const handleEditSave = async (groupName) => {
    if (!editQty) return toast.error("Quantity is required");
    try {
      const response = await axiosInstance.put(`/api/purchase/ocr-qty-master/${encodeURIComponent(groupName)}`, { qtyPerRow: editQty });
      if (response.data.success) {
        toast.success(response.data.message);
        setEditRow(null);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error updating record");
    }
  };

  const handleDelete = async (groupName) => {
    if (!window.confirm(`Are you sure you want to delete ${groupName}?`)) return;
    try {
      const response = await axiosInstance.delete(`/api/purchase/ocr-qty-master/${encodeURIComponent(groupName)}`);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error deleting record");
    }
  };

  const columns = [
    {
      name: "Group Name",
      selector: (row) => row.GROUPNAME,
      sortable: true,
      minWidth: "200px"
    },
    {
      name: "Qty / Row",
      minWidth: "150px",
      cell: (row) => (
        editRow === row.GROUPNAME ? (
          <input
            type="number"
            step="any"
            className="form-control form-control-sm text-center fw-bold text-primary"
            value={editQty}
            onChange={(e) => setEditQty(e.target.value)}
            autoFocus
          />
        ) : (
          <span className="fw-bold">{row.QTYPERROW}</span>
        )
      )
    },
    {
      name: "Action",
      minWidth: "150px",
      cell: (row) => (
        <div className="d-flex gap-2">
          {editRow === row.GROUPNAME ? (
            <>
              <button onClick={() => handleEditSave(row.GROUPNAME)} className="btn btn-sm btn-success p-1"><Save size={16}/></button>
              <button onClick={() => setEditRow(null)} className="btn btn-sm btn-secondary p-1"><X size={16}/></button>
            </>
          ) : (
            <>
              <button onClick={() => handleEditClick(row)} className="btn btn-sm text-primary p-0" title="Edit"><Edit size={18}/></button>
              <button onClick={() => handleDelete(row.GROUPNAME)} className="btn btn-sm text-danger p-0" title="Delete"><Trash2 size={18}/></button>
            </>
          )}
        </div>
      )
    }
  ];

  const filteredData = data.filter((item) =>
    item.GROUPNAME?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!show) return null;

  return (
    <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}>
      <div className="modal-dialog modal-dialog-centered modal-xl">
        <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden" style={{ maxHeight: "90vh" }}>
          
          {/* MODAL HEADER */}
          <div className="modal-header border-0 bg-light p-4 d-flex justify-content-between align-items-center">
            <div>
              <h4 className="fw-black mb-1 text-dark tracking-tight">OCR Qty Master</h4>
              <p className="text-muted small mb-0">Manage default item quantities per row</p>
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          {/* MODAL BODY */}
          <div className="modal-body p-4 bg-white" style={{ overflowY: "auto" }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="position-relative" style={{ maxWidth: "350px", width: "100%" }}>
                <span className="position-absolute top-50 translate-middle-y ms-3 text-muted"><Search size={16} /></span>
                <input
                  className="form-control ps-5 border-light bg-light"
                  placeholder="Search Group Name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <button className="btn btn-primary fw-bold px-4 d-flex align-items-center gap-2 rounded-3 shadow-sm" onClick={() => setShowAddModal(true)}>
                <Plus size={18} /> Add New Group
              </button>
            </div>

            <div className="card shadow-sm border-0 rounded-4">
              <DataTable
                columns={columns}
                data={filteredData}
                pagination
                progressPending={loading}
                highlightOnHover
                responsive
                customStyles={{
                  headCells: { style: { fontWeight: "800", color: "#64748b", textTransform: "uppercase", fontSize: "11px" } },
                  rows: { style: { minHeight: "60px", borderBottom: "1px solid #f1f5f9" } },
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ADD MODAL (nested inside) */}
      {showAddModal && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.6)", zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
              <div className="modal-header border-0 bg-primary text-white p-4">
                <h5 className="fw-bold mb-0 d-flex align-items-center gap-2"><Plus size={20} /> Add New Group</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowAddModal(false)}></button>
              </div>
              <form onSubmit={handleAddSubmit}>
                <div className="modal-body p-4 bg-light">
                  <div className="mb-3">
                    <label className="fw-bold text-muted small mb-1 text-uppercase">Group Name</label>
                    <input
                      type="text"
                      className="form-control fw-bold"
                      value={newGroup.groupName}
                      onChange={(e) => setNewGroup({ ...newGroup, groupName: e.target.value })}
                      required
                      placeholder="e.g. CARTON TAPE"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="fw-bold text-muted small mb-1 text-uppercase">Qty Per Row</label>
                    <input
                      type="number"
                      step="any"
                      className="form-control fw-bold text-primary"
                      value={newGroup.qtyPerRow}
                      onChange={(e) => setNewGroup({ ...newGroup, qtyPerRow: e.target.value })}
                      required
                      placeholder="e.g. 10"
                    />
                  </div>
                </div>
                <div className="modal-footer border-0 bg-white">
                  <button type="button" className="btn btn-light fw-bold" onClick={() => setShowAddModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary fw-bold px-4">Save</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OCRQtyMasterModal;
